import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Button,
  ButtonProps,
  buttonVariants,
} from "@/components/custom/button";
import { type VariantProps } from "class-variance-authority";
import { Dispatch, SetStateAction, useState } from "react";
import { IconType } from "react-icons/lib";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, render, Renderable } from "@/lib/utils";
import { PiPlus, PiCaretDown, PiMagicWand } from "react-icons/pi";
import { TooltipContent, TooltipProvider } from "./tooltip";

export type DialogAction = {
  label: string;
  title?: string;
  icon?: IconType;
  disabled?: boolean;
  content: Renderable<{ onClose: () => void }>;
  contentClassName?: string;
};

export type ButtonAction = {
  label: string;
  icon?: IconType;
  disabled?: boolean;
  onClick: () => void;
};

export type Action = DialogAction | ButtonAction;

// Type guards
const isDialogAction = (action: Action): action is DialogAction => {
  return "content" in action;
};

const isButtonAction = (action: Action): action is ButtonAction => {
  return "onClick" in action;
};

export default function DialogComponent({
  children,
  label,
  title,
  openState,
  icon: Icon,
  disabled = false,
  actions,
  variant = "azure",
  className,
  contentClassName,
  buttonProps,
}: {
  children?: React.ReactNode;
  label?: string;
  title?: string;
  openState?: [boolean, Dispatch<SetStateAction<boolean>>];
  icon?: IconType | null;
  disabled?: boolean;
  actions?: Action[];
  variant?: VariantProps<typeof buttonVariants>["variant"];
  className?: string;
  contentClassName?: string;
  buttonProps?: ButtonProps;
}) {
  const [selectedActionIndex, setSelectedActionIndex] = useState(0);
  const [internalOpen, setInternalOpen] = useState(false);
  const hasActions = actions && actions.length > 0;
  const currentAction = hasActions ? actions[selectedActionIndex] : null;

  // Use provided openState or internal state
  const [isOpen, setIsOpen] = openState || [internalOpen, setInternalOpen];

  // Use provided label/title or get from current action
  const displayLabel = label || currentAction?.label || "";
  const displayTitle =
    title ||
    (currentAction && isDialogAction(currentAction)
      ? currentAction.title
      : undefined) ||
    currentAction?.label ||
    "";
  const DisplayIcon = Icon || currentAction?.icon;
  const isDisabled = disabled || currentAction?.disabled || false;
  const renderTrigger = () => {
    // For multiple actions - show dropdown
    if (hasActions && actions.length > 1) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              {...buttonProps}
              className={cn(
                "flex gap-1 justify-between px-4",
                buttonProps?.className,
              )}
              variant={buttonProps?.variant ?? variant ?? "azure"}
              disabled={disabled}
            >
              <span className="flex items-center gap-1">
                {DisplayIcon ? (
                  <DisplayIcon className="w-4 h-4" />
                ) : Icon !== null ? (
                  <PiMagicWand className="w-4 h-4" />
                ) : null}
                {displayLabel || "Actions"}
              </span>
              <PiCaretDown className="w-4 h-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action, index) => (
              <DropdownMenuItem
                key={index}
                disabled={action.disabled}
                onClick={() => {
                  if (isButtonAction(action)) {
                    action.onClick();
                  } else {
                    setSelectedActionIndex(index);
                    setIsOpen(true);
                  }
                }}
                className="flex items-center gap-2"
              >
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    // For single action - show button
    return (
      <Button
        {...buttonProps}
        className={cn(
          "flex gap-1 justify-around px-4",
          buttonProps?.className,
          className,
        )}
        variant={buttonProps?.variant ?? variant}
        disabled={isDisabled}
      >
        {DisplayIcon ? (
          <DisplayIcon className="w-4 h-4" />
        ) : Icon !== null ? (
          <PiPlus className="w-4 h-4" />
        ) : null}
        {displayLabel}
      </Button>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {/* For multiple actions, render dropdown without DialogTrigger to avoid ref conflicts */}
      {hasActions && actions.length > 1 ? (
        renderTrigger()
      ) : (
        <DialogTrigger asChild>{renderTrigger()}</DialogTrigger>
      )}
      <DialogContent
        aria-describedby={undefined}
        className={cn(
          contentClassName,
          currentAction && isDialogAction(currentAction)
            ? currentAction?.contentClassName
            : undefined,
        )}
      >
        <TooltipProvider>
          <DialogHeader className="mb-4">
            <DialogTitle className="mb-4">{displayTitle}</DialogTitle>
            <hr />
          </DialogHeader>
          {hasActions && currentAction && isDialogAction(currentAction)
            ? render(currentAction.content, { onClose: () => setIsOpen(false) })
            : children}
          <TooltipContent />
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
